import { useMutation } from "@tanstack/react-query";
import {
  hasOfferBeenTracked,
  markOfferAsTracked,
  PixelTrackingData,
} from "../util/pixelTracking";

const DEBUG = process.env.NODE_ENV === "development";

/**
 * Fires the pixel API to track offer impressions
 */
async function firePixelAPI(data: PixelTrackingData): Promise<void> {
  const { memberId, holderRef, retailerId, storeId, offer, spotlight } = data;
  // Check if offer has been tracked recently
  if (offer?.id && hasOfferBeenTracked(offer.id, memberId)) {
    return;
  }

  // Pixel API configuration
  const PIXEL_API_URL = process.env.NEXT_PUBLIC_PIXEL_API_URL;
  const ENVIRONMENT = process.env.APP_ENV;
  const SUBSCRIPTION_KEY = process.env.NEXT_PUBLIC_SUBSCRIPTION_KEY;

  if (!PIXEL_API_URL || !SUBSCRIPTION_KEY) {
    return;
  }

  let objectToSend: Record<string, string | number | boolean | undefined> = {
    member_id: memberId,
    retailer_id: retailerId,
    cw_store_id: storeId,
    holder_ref: holderRef, // Card number/holder ref
    source: "cw_pixel_api",
  };

  if (offer) {
    const { id: offer_id, ended_at: offer_end_date } = offer;

    objectToSend = {
      ...objectToSend,
      offer_id,
      offer_end_date,
    };
  }

  if (spotlight) {
    const { id: spotlight_id, ended_at: spotlight_end_date } = spotlight;
    objectToSend = {
      ...objectToSend,
      spotlight_id,
      spotlight_end_date,
    };
  }

  // Prepare pixel data
  const pixelData = {
    e: ENVIRONMENT,
    cw_partner: "Welcia barcode",
    t: "pageview",
    ts: Date.now(),
    ca: [
      {
        ...objectToSend,
      },
    ],
  };

  const options = {
    method: "POST",
    headers: {
      Host: PIXEL_API_URL,
      "Ocp-Apim-Subscription-Key": SUBSCRIPTION_KEY,
      product: "AMK - Welcia",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(pixelData),
  };

  try {
    // Fire pixel API with timeout to prevent blocking
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await fetch(`${PIXEL_API_URL}?e=${ENVIRONMENT}`, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      // Mark as tracked only on success
      if (offer?.id) markOfferAsTracked(offer.id, memberId);
    }
  } catch (error) {
    if (DEBUG) {
      console.error(`Pixel API failed for offer ${offer?.id}:`, error);
    }
    // Don't mark as tracked on error - allow retry
  }
}

/**
 * React Query hook for tracking offer impressions with pixel API
 * Uses mutation to fire tracking events asynchronously without blocking UI
 */
export function usePixelTracking() {
  return useMutation({
    mutationFn: firePixelAPI,
    // No retry on failure - tracking is fire-and-forget
    retry: false,
    // Network mode online-only to avoid queueing tracking calls
    networkMode: "online",
  });
}
